import { CARD_FORMATS, USER_FORM_STEPS, USER_GROUPS } from '@/config/addUserFlow';
import { staticDefaults } from '@/config/static/catalog';
import type { UserCreationSuccess, UserDraft, UserFormStepId } from '@/types/userCreation';
import type { GuidedFlowDefinition } from './types';

const credentialOptions = [
  { value: 'card', label: 'Card' },
  { value: 'pin', label: 'PIN' },
  { value: 'card-and-pin', label: 'Fingerprint' },
];

export const userCreationFlow: GuidedFlowDefinition<UserDraft, UserFormStepId, UserCreationSuccess> = {
  id: 'user-creation',
  formTitle: 'Add user',
  systemLabelPrefix: 'Site',
  steps: USER_FORM_STEPS,
  schemas: {
    'user-details': {
      id: 'user-details',
      layout: 'grid',
      instruction: {
        operator: "Enter the new user's information. First and last name are required.",
        novice: 'Fill in user details to add them to the system.',
      },
      fields: [
        { id: 'firstName', type: 'text', label: 'First name', required: true, placeholder: 'Enter first name', aiField: 'firstName' },
        { id: 'lastName', type: 'text', label: 'Last name', required: true, placeholder: 'Enter last name', aiField: 'lastName' },
        { id: 'employmentNumber', type: 'text', label: 'Employment number', placeholder: 'Enter employment number', aiField: 'employmentNumber' },
        {
          id: 'userGroup',
          type: 'select',
          label: 'User group',
          options: USER_GROUPS.map((item) => ({ value: item, label: item })),
        },
        { id: 'email', type: 'email', label: 'Email', required: true, placeholder: 'Enter email', aiField: 'email' },
        { id: 'mobileNumber', type: 'tel', label: 'Mobile number', placeholder: 'Enter mobile number', aiField: 'mobileNumber' },
      ],
      backLabel: 'Cancel',
      nextLabel: 'Next',
    },
    credential: {
      id: 'credential',
      layout: 'credential',
      instruction: 'Choose the credential type, the fields below will update to match.',
      fields: [
        {
          id: 'credentialType',
          type: 'segmented',
          label: 'Credential type',
          options: credentialOptions,
        },
        {
          id: 'cardFormat',
          type: 'select',
          label: 'Format',
          required: true,
          aiField: 'cardFormat',
          options: CARD_FORMATS.map((item) => ({ value: item, label: item })),
        },
        { id: 'cardNumber', type: 'text', label: 'Number', required: true, placeholder: 'Enter card number', aiField: 'cardNumber' },
        { id: 'facilityCode', type: 'text', label: 'Facility code', placeholder: 'Enter facility code', aiField: 'facilityCode' },
        { id: 'pin', type: 'text', label: 'PIN', placeholder: 'Enter PIN' },
        { id: 'activationDate', type: 'date', label: 'Activation date' },
        { id: 'expirationDate', type: 'date', label: 'Expiration date', placeholder: 'Select expiration date' },
      ],
      backLabel: 'Back',
      nextLabel: 'Next',
    },
    'access-group': {
      id: 'access-group',
      layout: 'stack',
      instruction: 'Choose which doors this user can use. Select one or more groups.',
      fields: [
        {
          id: 'accessGroups',
          type: 'list-multi',
          label: 'Access groups',
          options: staticDefaults.user.accessGroups.map((item) => ({ value: item, label: item })),
        },
      ],
      backLabel: 'Back',
      nextLabel: 'Next',
    },
    confirm: {
      id: 'confirm',
      fields: [],
      backLabel: 'Back',
      nextLabel: 'Confirm',
    },
  },
  approvalRows: [
    { key: 'First name', value: (draft) => draft.firstName || '—', aiField: 'firstName' },
    { key: 'Last name', value: (draft) => draft.lastName || '—', aiField: 'lastName' },
    { key: 'Employment number', value: (draft) => draft.employmentNumber || '—', aiField: 'employmentNumber' },
    { key: 'Email', value: (draft) => draft.email || '—', aiField: 'email' },
    { key: 'Format', value: (draft) => draft.cardFormat || '—', aiField: 'cardFormat' },
    { key: 'Number', value: (draft) => draft.cardNumber || '—', aiField: 'cardNumber' },
  ],
  success: {
    bannerText: (result) => `${result.userName} added to this system.`,
    noteText: 'Added to this system. Door access applies after the card is active at the reader.',
  },
};
